import { isDefined } from "@clipboard-health/util-ts";
import { patch } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { GET_WORKER_QUERY_KEY } from "@src/appV2/Worker/api/constants";
import { type Worker } from "@src/appV2/Worker/api/types";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { generatePath } from "react-router-dom";
import { z } from "zod";

const archiveLicenseRequestSchema = z.object({
  status: z.literal("ARCHIVED"),
});

export type ArchiveLicenseRequest = z.infer<typeof archiveLicenseRequestSchema>;

export interface ArchiveLicenseParams {
  licenseId: string;
  workerId: string;
}

const ARCHIVE_LICENSE_PATH = "/workers/:workerId/licenses/:licenseId";

export function useArchiveLicense(
  options: UseMutationOptions<void, AxiosError, ArchiveLicenseParams> = {}
): UseMutationResult<void, AxiosError, ArchiveLicenseParams> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ArchiveLicenseParams) => {
      const { licenseId, workerId } = params;

      await patch({
        url: generatePath(
          `${environmentConfig.REACT_APP_LICENSE_MANAGER_SERVICE_URL}${ARCHIVE_LICENSE_PATH}`,
          {
            workerId,
            licenseId,
          }
        ),
        data: { status: "ARCHIVED" },
        requestSchema: archiveLicenseRequestSchema,
        responseSchema: z.unknown(),
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.ARCHIVE_LICENSE_FAILURE,
    },
    onSuccess: (_data, params) => {
      const { licenseId } = params;

      queryClient.setQueryData([GET_WORKER_QUERY_KEY], (oldData: Worker | undefined) => {
        if (!isDefined(oldData)) {
          return oldData;
        }

        return {
          ...oldData,
          licenses: oldData.licenses.filter((license) => license.id !== licenseId),
        };
      });
    },
    ...options,
  });
}
